import React from 'react';
import { useParams, Link as RouterLink, useSearchParams, Link } from 'react-router-dom';
import queryString from 'query-string';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
// @mui
import { styled } from '@mui/material/styles';
import {
  Button,
  Typography,
  Container,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
} from '@mui/material';
import { ReactFormBuilder, ReactFormGenerator } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
// components

import { updateSafalEventInvitePrivateResponse } from '../../../../services/Service';
import Page from '../../../../components/Page';
import '../DynamicForm/DynamicForm.css';
import './InviteResponse.css';

import Inlineloader from '../../../../components/Loader/InlineLoader';

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

const API_URL = process.env.REACT_APP_API_URL;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const isValidEmail = (email) => emailRegex.test(email);

export const InviteResponse = () => {
  const { code } = useParams();
  const { user } = useSelector((state) => state.login);
  const [searchParams, setSearchParams] = useSearchParams();
  const paramEmail = searchParams.get('email');
  const userEmail = user?.email;

  const [inviteInfo, setInviteInfo] = React.useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState({
    open: false,
    values: null,
  });
  const [isYouSubmitted, setIsYouSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const isThisPublic = inviteInfo?.onlyRSVP || user?._id;
  const expiryDate = inviteInfo?.expiryDate;
  const status = inviteInfo?.status;

  const getInviteInfo = async (data) => {
    setLoading(true);
    try {
      const queryParam = queryString.stringify(data, { skipNull: true, skipEmptyString: true });
      const res = await axios.get(`${API_URL}/safal-event/invite-info?${queryParam}`);
      setInviteInfo(res.data.data);
      const findYou = res.data.data?.people?.find((e) => e.email === paramEmail);
      if (findYou) findYou?.status === 'Accepted' && setIsYouSubmitted(true);
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getInviteInfo({ code: code });
  }, []);

  const onInviteSubmit = async (data, email) => {
    const newEmail = email || paramEmail;
    if (!newEmail) {
      setEmailDialogOpen({
        open: true,
        values: data,
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        _id: inviteInfo._id,
        email: newEmail,
        status: 'Accepted', // Accepted, Rejected
        formResponse: data,
      };
      const res = await axios.put(`${API_URL}/safal-event/invite-info`, payload);
      console.log('DEBUG res', res);
      setIsYouSubmitted(true);
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onInviteSubmitPrivate = async (data) => {
    setLoading(true);
    try {
      const payload = {
        _id: inviteInfo._id,
        email: paramEmail,
        status: 'Accepted', // Accepted, Rejected
        formResponse: data,
      };
      const res = await updateSafalEventInvitePrivateResponse(payload);
      console.log('DEBUG res', res);
      setIsYouSubmitted(true);
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onEmailDialogClose = (email) => {
    const values = emailDialogOpen.values;
    setEmailDialogOpen({
      open: false,
      values: null,
    });
    if (email) onInviteSubmit(values, email);
  };

  if (loading) return <Inlineloader />;
  if (!inviteInfo)
    return (
      <Page title="404 Page Not Found">
        <Container>
          <ContentStyle sx={{ textAlign: 'center', alignItems: 'center' }}>
            <Typography variant="h3" paragraph>
              Sorry, invite not found!
            </Typography>

            <Typography sx={{ color: 'text.secondary' }}>
              Sorry, we couldn't find the invite you're looking for.
            </Typography>
            <Button style={{ marginTop: '64px' }} to="/" size="large" variant="contained" component={RouterLink}>
              Go to Home
            </Button>
          </ContentStyle>
        </Container>
      </Page>
    );

  const _data = inviteInfo?.form?.element;

  return (
    <Page title="SafalEvent - RSVP">
      <div style={{ padding: '32px' }} id="DynamicFormDiv">
        <ReactFormGenerator
          data={_data}
          onSubmit={(values) => {
            user?._id ? onInviteSubmitPrivate(values) : onInviteSubmit(values);
          }}
          submitButton={
            <>
              {isThisPublic ? (
                <>
                  {status === 'Active' && !isYouSubmitted && (
                    <Button
                      type="submit"
                      disabled={loading}
                      sx={{ backgroundColor: '#3D71FF', borderRadius: '30px', textTransform: 'capitalize' }}
                      variant="contained"
                    >
                      Submit
                    </Button>
                  )}
                  {isYouSubmitted && (
                    <span>
                      Thank you for your response <br />
                      {user?._id ? <Link to="/">Go to Home</Link> : <Link to="/login">Login</Link>}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span>
                    To submit your RSVP, please <Link to="/login">Login</Link>.
                  </span>
                </>
              )}
            </>
          }
        />
      </div>
      <div id="InviteResponse-footer">
        <span>Created using SafalSubscriptions</span>
        <span>Copyright © 2025 SafalVir, Inc. All Rights Reserved</span>
        {/* <span>Terms of Condition | Privacy Policy</span> */}
      </div>
      <EmailDialog open={emailDialogOpen.open} onClose={onEmailDialogClose} />
    </Page>
  );
};

export default InviteResponse;

const EmailDialog = ({ onClose, open }) => {
  const [emailValue, setEmailValue] = React.useState('');
  const [emailError, setEmailError] = React.useState('');

  const handleClose = (email) => {
    onClose?.(email);
  };

  const onSubmit = () => {
    if (isValidEmail(emailValue)) {
      setEmailError('');
      onClose?.(emailValue);
      return;
    }
    setEmailError('Please enter a valid email address.');
  };

  return (
    <Dialog open={open} onClose={() => handleClose()}>
      <DialogTitle>RSVP</DialogTitle>
      <DialogContent>
        <DialogContentText>To submit to this RSVP, please enter your email address here.</DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          name="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="standard"
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          error={!!emailError}
          helperText={emailError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()}>Cancel</Button>
        <Button onClick={() => onSubmit()}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
};
